import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

export default class UnitAxesMesh extends THREE.Group {
  xArrow!: THREE.Mesh;
  yArrow!: THREE.Mesh;
  zArrow!: THREE.Mesh;
  isUnitAxesMesh = true;

  constructor(
    props: {
      xColor?: number;
      yColor?: number;
      zColor?: number;
      coneHeight?: number;
      radius?: number;
      clone?: UnitAxesMesh;
    } = {},
  ) {
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    const opts = {
      xColor: 0xff0000,
      yColor: 0x00ff00,
      zColor: 0x0000ff,
      coneHeight: 0.2,
      radius: 0.025,
      ...props,
    };

    super();

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Build Arrows

    if (opts.clone) {
      this.renderOrder = opts.clone.renderOrder;
      this.scale.copy(opts.clone.scale);
      this.position.copy(opts.clone.position);
      this.quaternion.copy(opts.clone.quaternion);

      const geo = opts.clone.xArrow.geometry;
      this.xArrow = new THREE.Mesh(geo, opts.clone.xArrow.material);
      this.xArrow.rotation.z = -Math.PI * 0.5;
      this.yArrow = new THREE.Mesh(geo, opts.clone.yArrow.material);
      this.zArrow = new THREE.Mesh(geo, opts.clone.zArrow.material);
      this.zArrow.rotation.x = Math.PI * 0.5;
    } else {
      const geo = unitArrowGeometry(opts);

      this.xArrow = new THREE.Mesh(
        geo,
        new THREE.MeshBasicMaterial({ color: opts.xColor }),
      );
      this.xArrow.rotation.z = -Math.PI * 0.5;

      this.yArrow = new THREE.Mesh(
        geo,
        new THREE.MeshBasicMaterial({ color: opts.yColor }),
      );

      this.zArrow = new THREE.Mesh(
        geo,
        new THREE.MeshBasicMaterial({ color: opts.zColor }),
      );
      this.zArrow.rotation.x = Math.PI * 0.5;
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Add children
    this.add(this.xArrow, this.yArrow, this.zArrow);
  }

  forDebugging(ord = 99): this {
    this.renderOrder = ord;
    this.xArrow.renderOrder = ord;
    this.yArrow.renderOrder = ord;
    this.zArrow.renderOrder = ord;

    (this.xArrow.material as any).depthTest = false;
    (this.yArrow.material as any).depthTest = false;
    (this.zArrow.material as any).depthTest = false;
    return this;
  }

  shallowClone(): UnitAxesMesh {
    return new UnitAxesMesh({ clone: this });
  }
}

function unitArrowGeometry(props: { coneHeight?: number; radius?: number }) {
  const opts = {
    coneHeight: 0.2,
    radius: 0.025,
    ...props,
  };

  const cylHeight = 1 - opts.coneHeight;

  // CONE
  const geoCone = new THREE.ConeGeometry(
    opts.radius * 2.5,
    opts.coneHeight,
    10,
  );
  geoCone.translate(0, cylHeight, 0);

  // CYLINDER
  const geoCyl = new THREE.CylinderGeometry(
    opts.radius,
    opts.radius,
    cylHeight,
    10,
  );
  geoCyl.translate(0, cylHeight * 0.5, 0);

  return mergeGeometries([geoCyl, geoCone]);
}
